<template>
  <div class="flashcards-v2-page px-2 pt-3">
    <v-container fluid fill-height>
      <!-- Diálogo de Sugestão / Reportar Card -->
      <v-dialog v-model="showSuggestionDialog" max-width="500">
        <v-card class="rounded-xl pa-2">
          <v-card-title class="d-flex align-center font-weight-bold" style="color: #0f172a;">
            <v-icon color="red" class="mr-2">mdi-flag-outline</v-icon>
            Reportar / Sugerir Melhoria
          </v-card-title>
          <v-card-text class="text-body-2 text-justify" style="color: #475569;">
            <div class="mb-2">
              <strong>Card ID:</strong> {{ currentFlashcard?.card_id || 'N/A' }}
            </div>
            Encontrou algum erro conceitual, ortográfico ou gostaria de sugerir melhoria neste card? Seu feedback ajuda a aprimorar nosso banco pedagógico.
            <v-textarea
              v-model="suggestionText"
              label="Descreva o problema ou sugestão"
              rows="4"
              variant="outlined"
              class="mt-3"
              :rules="[v => !!v || 'Campo obrigatório']"
            ></v-textarea>
          </v-card-text>
          <v-card-actions class="px-4 pb-3">
            <v-spacer></v-spacer>
            <v-btn variant="text" color="grey-darken-1" @click="closeSuggestionDialog">Cancelar</v-btn>
            <v-btn
              variant="elevated"
              color="primary"
              :disabled="!suggestionText || !suggestionText.trim() || isSubmittingSuggestion"
              :loading="isSubmittingSuggestion"
              @click="saveSuggestion"
            >
              Enviar Sugestão
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Container Centralizado dos Flashcards -->
      <div class="d-flex justify-center w-100">
        <v-row justify="center" class="w-100">
          <v-col cols="12" sm="10" md="8" lg="6" class="text-center pt-0" style="max-width: 600px;">
            <!-- Barra de Progresso do Lote de 25 Cards -->
            <div class="mb-3 text-left">
              <div class="d-flex justify-space-between align-center mb-1 text-caption font-weight-bold" style="color: #64748b;">
                <span>
                  {{ activeDeck?.title || 'Flashcards 2.0' }}
                </span>
                <span v-if="!isFinished">
                  Card {{ currentFlashcardIndex + 1 }} de {{ totalCardsInBatch }}
                </span>
                <span v-else>
                  Concluído
                </span>
              </div>
              <v-progress-linear
                class="progress-bar rounded-pill"
                height="8"
                :model-value="progressPercentage"
                color="#315fd8"
                bg-color="#e2e8f0"
              ></v-progress-linear>
            </div>

            <!-- CARD ATIVO EM ESTUDO -->
            <v-card
              v-if="!isFinished && currentFlashcard"
              class="flashcard-card pa-4 position-relative mx-auto elevation-2"
              style="min-height: 65vh; max-height: 75vh; overflow-y: auto; display: flex; flex-direction: column; justify-content: space-between;"
            >
              <!-- Botão de Reportar no Topo Direito -->
              <v-btn
                icon
                size="x-small"
                variant="text"
                color="red"
                class="report-btn"
                title="Reportar problema neste card"
                @click="openSuggestionDialog"
              >
                <v-icon size="18">mdi-flag-outline</v-icon>
              </v-btn>

              <!-- Conteúdo Superior: Badges e Subtema -->
              <div class="text-left w-100 mb-3 pr-8">
                <v-chip
                  size="x-small"
                  :color="getTypeBadgeColor(currentFlashcard.tipo)"
                  variant="flat"
                  class="font-weight-bold mr-2 text-uppercase"
                >
                  <v-icon start size="12">{{ getTypeBadgeIcon(currentFlashcard.tipo) }}</v-icon>
                  {{ getTypeBadgeLabel(currentFlashcard.tipo) }}
                </v-chip>
                <span v-if="currentFlashcard.subtema" class="text-caption text-medium-emphasis">
                  {{ currentFlashcard.subtema }}
                </span>
              </div>

              <!-- FRENTE DO CARD (Pergunta / Caso / Cloze) -->
              <div class="card-body-content my-auto px-2">
                <template v-if="!showAnswer">
                  <!-- Renderização para tipo CLOZE na Frente -->
                  <div
                    v-if="currentFlashcard.tipo === 'cloze'"
                    class="card-text-frente text-h6 font-weight-bold"
                    v-html="formatClozeFront(currentFlashcard.frente)"
                  ></div>

                  <!-- Renderização para tipo MINI_CASO na Frente -->
                  <div v-else-if="currentFlashcard.tipo === 'mini_caso'" class="text-left">
                    <div class="card-text-frente text-body-1 font-weight-medium" style="white-space: pre-line; line-height: 1.6; color: #1e293b;">
                      {{ currentFlashcard.frente }}
                    </div>
                  </div>

                  <!-- Renderização BÁSICA na Frente -->
                  <div v-else class="card-text-frente text-h6 font-weight-bold" style="white-space: pre-line; color: #1e293b;">
                    {{ currentFlashcard.frente }}
                  </div>

                  <!-- Imagem do Estímulo na Frente se houver -->
                  <v-img
                    v-if="currentFlashcard.questionImage"
                    :src="currentFlashcard.questionImage"
                    max-height="220"
                    contain
                    class="mt-4 rounded-lg mx-auto"
                  ></v-img>
                </template>

                <!-- VERSO DO CARD (Resposta + Por quê) -->
                <template v-else>
                  <!-- Contexto da Frente no Topo -->
                  <div class="context-frente text-caption text-left text-medium-emphasis mb-3 px-2 py-1 rounded" style="background: #f1f5f9;">
                    <div class="font-weight-bold text-uppercase" style="font-size: 10px;">Pergunta:</div>
                    <div class="text-truncate">{{ currentFlashcard.frente }}</div>
                  </div>

                  <v-divider class="my-3"></v-divider>

                  <!-- Resposta / Verso -->
                  <div class="text-left px-1">
                    <div class="text-caption font-weight-bold text-uppercase" style="color: #315fd8; letter-spacing: 0.05em;">
                      Resposta:
                    </div>
                    <div
                      class="card-text-verso text-h6 font-weight-bold mt-1"
                      style="color: #0f172a; white-space: pre-line; line-height: 1.5;"
                    >
                      {{ currentFlashcard.verso }}
                    </div>
                  </div>

                  <!-- Imagem da Resposta se houver -->
                  <v-img
                    v-if="currentFlashcard.answerImage"
                    :src="currentFlashcard.answerImage"
                    max-height="200"
                    contain
                    class="mt-3 rounded-lg mx-auto"
                  ></v-img>

                  <!-- BOTÃO EXPANSÍVEL "POR QUÊ" (Justificativa Pedagógica) -->
                  <div v-if="currentFlashcard.por_que && currentFlashcard.por_que.trim() !== ''" class="mt-4 text-left">
                    <v-btn
                      variant="tonal"
                      color="primary"
                      size="small"
                      class="text-none font-weight-bold rounded-pill"
                      @click="showExplanation = !showExplanation"
                    >
                      <v-icon start size="16">{{ showExplanation ? 'mdi-chevron-up' : 'mdi-lightbulb-on-outline' }}</v-icon>
                      {{ showExplanation ? 'Ocultar Justificativa (Por quê)' : 'Por quê? (Ver explicação)' }}
                    </v-btn>

                    <v-expand-transition>
                      <v-card v-show="showExplanation" class="mt-2 pa-3 rounded-lg explanation-box" variant="tonal" color="primary">
                        <div class="d-flex align-center font-weight-bold text-caption mb-1" style="color: #1e3a8a;">
                          <v-icon size="14" color="#1e3a8a" class="mr-1">mdi-school-outline</v-icon>
                          Justificativa Pedagógica:
                        </div>
                        <div class="text-body-2" style="color: #1e293b; line-height: 1.5; white-space: pre-line;">
                          {{ currentFlashcard.por_que }}
                        </div>
                      </v-card>
                    </v-expand-transition>
                  </div>
                </template>
              </div>

              <!-- Ações da Base do Card -->
              <div class="mt-4 w-100">
                <!-- Se a resposta AINDA NÃO está aberta -->
                <div v-if="!showAnswer">
                  <v-btn
                    block
                    color="primary"
                    size="large"
                    elevation="2"
                    class="font-weight-bold text-none rounded-pill"
                    @click="showAnswer = true"
                  >
                    <v-icon start>mdi-eye-outline</v-icon>
                    Mostrar Resposta
                  </v-btn>
                </div>

                <!-- Se a resposta JÁ ESTÁ aberta: Botões SM-2 de Dificuldade -->
                <div v-else>
                  <div class="text-caption font-weight-bold text-medium-emphasis mb-2">
                    Como foi sua retenção neste card?
                  </div>
                  <v-row dense class="justify-center">
                    <v-col cols="3">
                      <v-btn
                        block
                        size="small"
                        color="error"
                        variant="outlined"
                        class="px-1 text-none font-weight-bold sm2-btn"
                        @click="handleSM2Grade(1)"
                        title="Errei ou achei muito difícil (revisar amanhã)"
                      >
                        Muito<br>difícil
                      </v-btn>
                    </v-col>
                    <v-col cols="3">
                      <v-btn
                        block
                        size="small"
                        color="warning"
                        variant="outlined"
                        class="px-1 text-none font-weight-bold sm2-btn"
                        @click="handleSM2Grade(3)"
                        title="Acertei com dificuldade"
                      >
                        Difícil
                      </v-btn>
                    </v-col>
                    <v-col cols="3">
                      <v-btn
                        block
                        size="small"
                        color="success"
                        variant="outlined"
                        class="px-1 text-none font-weight-bold sm2-btn"
                        @click="handleSM2Grade(4)"
                        title="Acertei com pouca hesitação"
                      >
                        Fácil
                      </v-btn>
                    </v-col>
                    <v-col cols="3">
                      <v-btn
                        block
                        size="small"
                        color="info"
                        variant="outlined"
                        class="px-1 text-none font-weight-bold sm2-btn"
                        @click="handleSM2Grade(5)"
                        title="Resposta imediata e perfeita"
                      >
                        Muito<br>fácil
                      </v-btn>
                    </v-col>
                  </v-row>

                  <v-btn
                    variant="text"
                    size="x-small"
                    color="grey-darken-1"
                    class="mt-2 text-none"
                    @click="showAnswer = false"
                  >
                    <v-icon start size="14">mdi-arrow-left</v-icon>
                    Voltar para Pergunta
                  </v-btn>
                </div>
              </div>
            </v-card>

            <!-- TELA DE SESSÃO CONCLUÍDA -->
            <v-card
              v-else-if="isFinished"
              class="flashcard-card pa-8 mx-auto elevation-2 text-center"
              style="min-height: 60vh; display: flex; flex-direction: column; justify-content: center; align-items: center;"
            >
              <v-avatar size="72" color="rgba(49, 95, 216, 0.1)" class="mb-4">
                <v-icon size="42" color="#315fd8">mdi-check-decagram</v-icon>
              </v-avatar>

              <h2 class="text-h5 font-weight-bold mb-2" style="color: #0f172a;">
                Lote de Estudos Concluído!
              </h2>

              <p class="text-body-1 text-medium-emphasis mb-6" style="max-width: 420px;">
                Você revisou com sucesso <strong>{{ reviewedInSessionCount }}</strong> flashcard(s) neste lote. O algoritmo SM-2 atualizou o agendamento das suas próximas revisões.
              </p>

              <div class="d-flex flex-column align-center w-100" style="max-width: 320px;">
                <!-- Botão Mais 25 cards se houver pool restante -->
                <v-btn
                  v-if="remainingPoolCount > 0"
                  block
                  color="primary"
                  size="large"
                  class="font-weight-bold text-none mb-3 rounded-pill"
                  elevation="2"
                  @click="loadNextBatch"
                >
                  <v-icon start>mdi-plus-circle-outline</v-icon>
                  Estudar Mais {{ Math.min(25, remainingPoolCount) }} Cards ({{ remainingPoolCount }} restantes)
                </v-btn>

                <!-- Botão Voltar aos Decks -->
                <v-btn
                  block
                  variant="outlined"
                  color="primary"
                  size="large"
                  class="font-weight-bold text-none rounded-pill"
                  @click="$router.push('/webapp/decksv2')"
                >
                  <v-icon start>mdi-arrow-left</v-icon>
                  Voltar para Decks 2.0
                </v-btn>
              </div>
            </v-card>

            <!-- TELA DE NENHUM CARD ENCONTRADO -->
            <v-card
              v-else
              class="flashcard-card pa-8 mx-auto elevation-1 text-center"
              style="min-height: 50vh; display: flex; flex-direction: column; justify-content: center; align-items: center;"
            >
              <v-icon size="48" color="grey" class="mb-3">mdi-cards-variant</v-icon>
              <h3 class="text-h6 font-weight-bold text-medium-emphasis mb-4">
                Nenhum card disponível neste deck no momento.
              </h3>
              <v-btn color="primary" variant="outlined" @click="$router.push('/webapp/decksv2')">
                Voltar aos Decks 2.0
              </v-btn>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </v-container>
  </div>
</template>

<script>
import { calculateSM2 } from '@/utils/sm2';

export default {
  name: 'FlashCardsV2',
  data() {
    return {
      currentFlashcardIndex: 0,
      showAnswer: false,
      showExplanation: false,
      difficultyTrackingV2: [], // Lista acumulada de avaliações SM-2 desta sessão
      reviewedInSessionCount: 0,
      showSuggestionDialog: false,
      suggestionText: '',
      isSubmittingSuggestion: false,
      isFinished: false,
    };
  },
  computed: {
    flashcards() {
      return this.$store.getters.flashcardsV2 || [];
    },
    activeDeck() {
      return this.$store.getters.activeDeckV2;
    },
    totalCardsInBatch() {
      return this.flashcards.length;
    },
    remainingPoolCount() {
      const pool = this.$store.getters.deckCardsPoolV2;
      return pool ? pool.length : 0;
    },
    currentFlashcard() {
      if (this.currentFlashcardIndex >= 0 && this.currentFlashcardIndex < this.flashcards.length) {
        return this.flashcards[this.currentFlashcardIndex];
      }
      return null;
    },
    progressPercentage() {
      if (!this.totalCardsInBatch) return 0;
      if (this.isFinished) return 100;
      return Math.min(100, Math.round(((this.currentFlashcardIndex + 1) / this.totalCardsInBatch) * 100));
    },
  },
  created() {
    if (!this.flashcards || this.flashcards.length === 0) {
      // Se a página for acessada diretamente sem seleção prévia, redireciona para Decks 2.0
      this.$router.replace('/webapp/decksv2');
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeyDown);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
    // Garante persistência imediata no Firestore caso o aluno feche ou mude de aba no meio do lote
    if (this.difficultyTrackingV2 && this.difficultyTrackingV2.length > 0) {
      this.persistBatch();
    }
  },
  methods: {
    getTypeBadgeLabel(tipo) {
      switch (tipo) {
        case 'cloze':
          return 'Complete a Lacuna';
        case 'mini_caso':
          return 'Caso Clínico';
        case 'basico':
        default:
          return 'Básico';
      }
    },
    getTypeBadgeColor(tipo) {
      switch (tipo) {
        case 'cloze':
          return 'deep-purple-darken-1';
        case 'mini_caso':
          return 'teal-darken-2';
        case 'basico':
        default:
          return 'primary';
      }
    },
    getTypeBadgeIcon(tipo) {
      switch (tipo) {
        case 'cloze':
          return 'mdi-form-textbox';
        case 'mini_caso':
          return 'mdi-clipboard-text-outline';
        case 'basico':
        default:
          return 'mdi-card-text-outline';
      }
    },
    formatClozeFront(text) {
      if (!text) return '';
      const str = String(text);
      // Escapa caracteres HTML perigosos preservando operadores matemáticos como < e >
      const escaped = str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      // Suporta variações como [...], [ ... ] e reticências unicode […]
      return escaped.replace(
        /\[\s*(?:\.\.\.|\u2026)\s*\]/g,
        '<span class="cloze-gap font-weight-bold px-2 py-0.5 mx-1 rounded bg-indigo-lighten-4 text-indigo-darken-3">[ &hellip; ]</span>'
      );
    },
    handleKeyDown(event) {
      if (this.showSuggestionDialog || this.isFinished) return;
      if (['INPUT', 'TEXTAREA'].includes(event.target?.tagName)) return;

      if ((event.code === 'Space' || event.code === 'Enter') && !this.showAnswer) {
        event.preventDefault();
        this.showAnswer = true;
      } else if (this.showAnswer) {
        if (event.key === '1') this.handleSM2Grade(1);
        else if (event.key === '2' || event.key === '3') this.handleSM2Grade(3);
        else if (event.key === '4') this.handleSM2Grade(4);
        else if (event.key === '5') this.handleSM2Grade(5);
      }
    },
    openSuggestionDialog() {
      this.suggestionText = '';
      this.showSuggestionDialog = true;
    },
    closeSuggestionDialog() {
      this.showSuggestionDialog = false;
      this.suggestionText = '';
    },
    saveSuggestion() {
      if (!this.currentFlashcard || !this.suggestionText || !this.suggestionText.trim()) return;

      const cardId = this.currentFlashcard.card_id || this.currentFlashcard.id || this.currentFlashcard.refId || 'N/A';
      this.isSubmittingSuggestion = true;

      const payload = {
        card_id: cardId,
        deck_id: this.currentFlashcard.deck_id || '',
        frente: this.currentFlashcard.frente || '',
        suggestionText: this.suggestionText.trim(),
      };

      this.$store.dispatch('postSuggestionFlashcardsV2', payload)
        .then(() => {
          this.closeSuggestionDialog();
        })
        .catch(err => {
          console.error('[FlashCards 2.0] Erro ao enviar sugestão:', err);
          this.closeSuggestionDialog();
        })
        .finally(() => {
          this.isSubmittingSuggestion = false;
        });
    },
    // Avaliação de Retenção com SM-2
    handleSM2Grade(grade) {
      if (!this.currentFlashcard) return;

      const cardId = this.currentFlashcard.card_id;
      const previousSM2 = this.currentFlashcard.sm2Record || {};

      // Executa o cálculo SM-2
      const sm2Result = calculateSM2(grade, previousSM2);

      const record = {
        card_id: cardId,
        refId: cardId,
        deck_id: this.currentFlashcard.deck_id,
        tipo: this.currentFlashcard.tipo,
        ...sm2Result,
      };

      this.difficultyTrackingV2.push(record);
      this.reviewedInSessionCount += 1;

      // Avança para o próximo card
      this.moveToNextCard();
    },
    moveToNextCard() {
      this.showAnswer = false;
      this.showExplanation = false;
      this.currentFlashcardIndex += 1;

      // Gravação intermediária a cada 5 cards respondidos
      if (
        this.currentFlashcardIndex % 5 === 0 &&
        this.currentFlashcardIndex < this.flashcards.length
      ) {
        this.persistBatch();
      }

      // Ao atingir o fim dos 25 cards
      if (this.currentFlashcardIndex >= this.flashcards.length) {
        this.persistBatch();
        this.isFinished = true;
      }
    },
    persistBatch() {
      if (!this.difficultyTrackingV2 || this.difficultyTrackingV2.length === 0) return;

      // Drenagem atômica síncrona do buffer para evitar race condition
      const toSave = this.difficultyTrackingV2.splice(0, this.difficultyTrackingV2.length);
      const payload = {
        total: toSave.length,
        difficultyTrackingV2: toSave,
      };

      this.$store.dispatch('saveCardsV2', payload)
        .then(() => {
          console.log(`[FlashCards 2.0] ${payload.total} cards persistidos com sucesso no Firestore.`);
        })
        .catch(err => {
          console.error('[FlashCards 2.0] Erro ao persistir lote no Firestore:', err);
          // Recoloca os itens no buffer em caso de erro na rede
          this.difficultyTrackingV2.unshift(...toSave);
        });
    },
    loadNextBatch() {
      this.$store.dispatch('generateCardsV2', { isContinue: true })
        .then(() => {
          this.currentFlashcardIndex = 0;
          this.showAnswer = false;
          this.showExplanation = false;
          this.isFinished = false;
          this.reviewedInSessionCount = 0; // Reinicia contador específico do novo lote
        });
    },
  },
};
</script>

<style scoped>
.flashcards-v2-page {
  background-color: #f8fafc;
  min-height: calc(100vh - 64px);
}

.flashcard-card {
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
}

.report-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  background-color: rgba(239, 68, 68, 0.08);
  border-radius: 6px;
}

.card-text-frente {
  line-height: 1.5;
}

.explanation-box {
  border: 1px solid rgba(49, 95, 216, 0.2);
}

.sm2-btn {
  height: 48px !important;
  line-height: 1.2 !important;
  font-size: 11px !important;
}
</style>

